import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import {ITableConfigProp} from "components/dashboard/data-input-output-table/common/api/interfaces/interfaces";
import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {Chips} from "primereact/chips";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import React from "react";
import { T } from "services/lang/LanguageService";
import { handleRowReorderEvt } from "utils/utils";

const TYPE_OPTIONS = [
    {value: "NUMBER"},
    {value: "BOOLEAN"},
    {value: "ENUM"}
];

interface DatasetConfigEditorProps {
    currentConfig: ITableConfigProp;
    setCurrentConfig: React.Dispatch<React.SetStateAction<ITableConfigProp>>;
    onConfigChange: (config: ITableConfigProp) => void;
}

const DatasetConfigEditor = ({currentConfig, setCurrentConfig, onConfigChange}: DatasetConfigEditorProps) => {
    const onRowEditComplete = (e) => {
        const datasetsToUpdate = [...currentConfig.datasets];
        const {newData, index} = e;

        datasetsToUpdate[index] = newData;
        setCurrentConfig({...currentConfig, datasets: datasetsToUpdate});
        onConfigChange({...currentConfig, datasets: datasetsToUpdate});

    };

    const textEditor = (options) => {
        return <InputText
            value={options.value}
            onChange={(e) => options.editorCallback(e.target.value)}
            style={{width: "100%"}}
        />;
    };

    const numberEditor = (options) => {
        return <InputNumber
            value={options.value}
            onChange={(e) => options.editorCallback(e.value)}
            inputStyle={{width: "100%"}}
        />;
    };

    const typeEditor = (options) => {
        const handleTypeChange = (e) => {
            const datasetsToUpdate = [...currentConfig.datasets];
            datasetsToUpdate[options.rowIndex] = {
                ...datasetsToUpdate[options.rowIndex],
                type: e.target.value
            };

            if (e.target.value.toUpperCase() !== "ENUM") {
                delete datasetsToUpdate[options.rowIndex].enum;
            }

            setCurrentConfig({...currentConfig, datasets: datasetsToUpdate});
            onConfigChange({...currentConfig, datasets: datasetsToUpdate});
            options.editorCallback(e.target.value);
        };

        return <Dropdown
            value={options.value}
            onChange={handleTypeChange}
            options={TYPE_OPTIONS}
            optionLabel="value"
            placeholder={T("action.select-type")}
            style={{width: "100%"}}
            showClear
        />;
    };

    const enumEditor = options => {
      if (currentConfig.datasets[options.rowIndex].type?.toUpperCase() === "ENUM") {
        const enumArray = options.value
          ? Object.entries(options.value).map(([key, value]) => `${key}:${value}`)
          : [];

        const handleEnumChange = e => {
          // Validate the entered values
          const isValid = e.value.every(item => {
            const [key, value] = item.split(":");
            return key && !isNaN(Number(value));
          });

          if (isValid) {
            const enumObject = e.value.reduce((obj, item) => {
              const [key, value] = item.split(":");
              obj[key] = Number(value);
              return obj;
            }, {});

            options.editorCallback(enumObject);
          } else {
            easasAlert({
              summary: T("alert.invalid-enum"),
              detail: T("alert.please-enter-key-value-pairs"),
              severity: "warn",
            });
          }
        };

        return <Chips value={enumArray} onChange={handleEnumChange} placeholder={T("word.key-value")} />;
      }
      return null;
    };

    const enumBodyTemplate = (rowData) => {
        if (rowData.type?.toUpperCase() === "ENUM") {
            return rowData.enum ? Object.entries(rowData.enum).map(([key, value]) => {
                return <div key={key}>{key}: {value}</div>;
            }) : T("word.no-enum");
        } 
            return null;
        
    };

    const titleBodyTemplate = (rowData: any) => {
        return (            
              <DatasetTitle identifier={rowData.identifier} />
        );        
}

    return (
        <DataTable
            value={currentConfig.datasets}
            editMode="row"
            onRowEditComplete={onRowEditComplete}
            tableStyle={{width: "100%"}}
            onRowReorder={handleRowReorderEvt(currentConfig.datasets, setCurrentConfig, currentConfig, 'datasets')}
            reorderableRows
        >
            <Column rowReorder style={{width: "3%"}}/>
            <Column
                className="p-0"
                field="identifier"
                header={T("word.title")}
                style={{width: "25%"}}
                align="center"
                body={titleBodyTemplate}
            />
            <Column
                className="p-0"
                field="decimalPlaces"
                header={T("word.decimal")}
                style={{width: "5%"}}
                align="center"
                editor={options => numberEditor(options)}
            />
            <Column
                className="p-0"
                field="overriddenTitle"
                header={T("word.overridden-title")}
                style={{width: "12%"}}
                align="center"
                editor={(options) => textEditor(options)}
            />
            <Column
                className="p-0"
                field="description"
                header={T("word.description")}
                style={{width: "25%"}}
                align="center"
                editor={(options) => textEditor(options)}
            />
            <Column
                className="p-0"
                field="type"
                header={T("word.type")}
                style={{width: "10%"}}
                align="center"
                editor={(options) => typeEditor(options)}
            />
            <Column
                className="p-0"
                field="enum"
                header="Enum"
                style={{width: "10%"}}
                align="center"
                editor={(options) => enumEditor(options)}
                body={enumBodyTemplate}
            />
            <Column
                className="p-0"
                rowEditor
                style={{width: "10%"}}
                headerStyle={{width: "10%"}}
                bodyStyle={{textAlign: "center"}}
            />
        </DataTable>
    );
};

export default DatasetConfigEditor;